import type { DateRange } from '@/components/analytics';
import type { ChannelGrowthDto , AnalyticsControllerGetListGrowthGranularity } from '@/apis/backend/generated/models';

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createFileRoute } from '@tanstack/react-router';
import { useOrganization } from '@/context/organization-context';
import { useAnalyticsControllerGetListGrowth } from '@/apis/backend/generated/analytics/analytics';
import {
  MetricCard,
  PeriodSelector,
  TimeSeriesChart,
  useDefaultDateRange,
  SourceBreakdownTable,
} from '@/components/analytics';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Skeleton from '@mui/material/Skeleton';
import Typography from '@mui/material/Typography';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';

export const Route = createFileRoute('/_authed/dashboard/o/$orgId/c/$clientId/analytics/list-growth')({
  component: ListGrowthPage,
});

function ListGrowthPage() {
  const { t } = useTranslation('analytics');
  const { activeOrganization, activeClient } = useOrganization();
  const defaultRange = useDefaultDateRange(30);
  const [dateRange, setDateRange] = useState<DateRange>(defaultRange);
  const [granularity, setGranularity] = useState<AnalyticsControllerGetListGrowthGranularity>('day');

  const orgId = activeOrganization?.id;
  const clientId = activeClient?.id;

  const { data, isLoading } = useAnalyticsControllerGetListGrowth(
    {
      orgId: orgId!,
      clientId: clientId!,
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
      previousStartDate: dateRange.previousStartDate,
      previousEndDate: dateRange.previousEndDate,
      granularity,
    },
    { query: { enabled: !!orgId && !!clientId, staleTime: 60000 } }
  );

  if (!orgId || !clientId) return null;

  return (
    <Stack spacing={3}>
      <Stack direction="row" spacing={2} alignItems="center">
        <PeriodSelector value={dateRange} onChange={setDateRange} />
        <ToggleButtonGroup
          value={granularity}
          exclusive
          onChange={(_, val) => val && setGranularity(val)}
          size="small"
        >
          <ToggleButton value="day" sx={{ textTransform: 'none' }}>{t('listGrowth.granularity.day', 'Day')}</ToggleButton>
          <ToggleButton value="week" sx={{ textTransform: 'none' }}>{t('listGrowth.granularity.week', 'Week')}</ToggleButton>
          <ToggleButton value="month" sx={{ textTransform: 'none' }}>{t('listGrowth.granularity.month', 'Month')}</ToggleButton>
        </ToggleButtonGroup>
      </Stack>

      {isLoading ? (
        <Stack spacing={2}>
          <Stack direction="row" spacing={2}>
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} variant="rounded" height={100} sx={{ flex: 1, borderRadius: '12px' }} />
            ))}
          </Stack>
          <Skeleton variant="rounded" height={350} sx={{ borderRadius: '12px' }} />
        </Stack>
      ) : data ? (
        <>
          <ChannelSection
            title={t('listGrowth.email.title', 'Email Subscriber Growth')}
            chartTitle={t('listGrowth.email.chartTitle', 'Email Subscriber Over Time')}
            channel={data.email}
          />
          <ChannelSection
            title={t('listGrowth.sms.title', 'SMS Subscriber Growth')}
            chartTitle={t('listGrowth.sms.chartTitle', 'SMS Subscriber Over Time')}
            channel={data.sms}
          />
        </>
      ) : (
        <Typography color="text.secondary">{t('listGrowth.empty', 'No list growth data available for this period.')}</Typography>
      )}
    </Stack>
  );
}

function ChannelSection({ title, chartTitle, channel }: { title: string; chartTitle: string; channel: ChannelGrowthDto }) {
  const { t } = useTranslation('analytics');
  return (
    <Stack spacing={2}>
      <Typography variant="h6" sx={{ fontWeight: 600 }}>
        {title}
      </Typography>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 2 }}>
        <MetricCard title={t('listGrowth.metrics.netSubscribers', 'Net Subscribers')} metric={channel.netSubscribers} />
        <MetricCard title={t('listGrowth.metrics.subscribers', 'Subscribers')} metric={channel.subscribers} />
        <MetricCard title={t('listGrowth.metrics.unsubscribes', 'Unsubscribes')} metric={channel.unsubscribes} invertTrend />
      </Box>

      {channel.timeSeries && channel.timeSeries.length > 0 && (
        <TimeSeriesChart
          title={chartTitle}
          data={channel.timeSeries}
          series={[
            { dataKey: 'subscribersAdded', name: t('listGrowth.series.subscribersAdded', 'Subscribers Added'), type: 'bar', color: '#4caf50', yAxisId: 'left' },
            { dataKey: 'unsubscribes', name: t('listGrowth.series.unsubscribes', 'Unsubscribes'), type: 'bar', color: '#ef9a9a', yAxisId: 'left' },
            { dataKey: 'netSubscribers', name: t('listGrowth.series.netSubscribers', 'Net Subscribers'), type: 'line', color: '#1565c0', yAxisId: 'right' },
          ]}
          formatRightAxis={(v) => v.toLocaleString()}
          formatTooltip={(value, name) => value.toLocaleString()}
        />
      )}

      {channel.sourceBreakdown && (
        <SourceBreakdownTable
          title={t('listGrowth.sourcesTitle', 'Subscriber Sources')}
          sources={channel.sourceBreakdown}
        />
      )}
    </Stack>
  );
}
